import type { Middleware } from '@reduxjs/toolkit';
import { isRejectedWithValue } from '@reduxjs/toolkit';
import { notifyError } from 'components/common/Toast/Toast';
import { apiSlice } from 'store/api/apiSlice';
import { authApi } from 'store/auth/authApi';
import { calendarApiSlice } from 'store/calendar/calendarApi';
import { clinicalToolsApi } from 'store/clinicalTools/clinicalToolsApi';
import { crossSellApi } from 'store/crossSell/crossSellApi';
import { invoicesApi } from 'store/patients/invoices/invoicesApi';
import { refundWorkflowApi } from 'store/patients/invoices/refundWorkflowApi';
import { patientsApi } from 'store/patients/patientApi';
import { patientPaymentApi } from 'store/patients/patientPayment/patientPaymentApi';
import { patientSubscriptionApi } from 'store/patients/patientSubscription/patientSubscriptionApi';
import { isCdsBlock } from 'store/pdmp/pdmp-attestation.utils';
import { pdmpApi } from 'store/pdmp/pdmpApi';
import { shopPagesApi } from 'store/shopPages/shopPagesApi';
import { shopSegmentsApi } from 'store/shopSegments/shopSegmentsApi';
import { tasksApi } from 'store/tasks/tasksSlice';
import { readRefundSupervisorApprovalRejection } from 'utils/refunds/supervisorApproval';

import { ActionPayload, getIdempotencyGuardCode } from './errorCatchingMiddleware.settings';

export type Action = {
  meta: {
    arg: {
      endpointName: string;
      originalArgs: unknown;
      track?: boolean;
      type: 'query' | 'mutation';
    };
    baseQueryMeta?: {
      request: Request;
      response?: Response;
    };
    rejectedWithValue: boolean;
    requestId: string;
  };
  payload: ActionPayload | { message: string };
  type: string;
};

const expectedActions = [
  calendarApiSlice.endpoints.createEvent,
  calendarApiSlice.endpoints.editEvent,
  // Medication template mutations handle their own validation error display
  // (structured field-level toasts). The global raw-string handler must be
  // suppressed for these endpoints to avoid double-firing.
  clinicalToolsApi.endpoints.requestMedicationTemplateCreation,
  clinicalToolsApi.endpoints.requestMedicationTemplateUpdate,
  // Partner coupon config writes map a 422's `errors` back to the row that caused it. The global
  // handler would additionally toast the body's summary `message`, duplicating what the row says.
  clinicalToolsApi.endpoints.createPartnerCouponConfig,
  clinicalToolsApi.endpoints.updatePartnerCouponConfigStatus,
  // Refund recommendation workflows own contextual error presentation: the
  // invoice form keeps a persistent inline explanation and the patient flow
  // opens RefundError. A global toast would duplicate either one.
  invoicesApi.endpoints.calculateRefundRecommendation,
  invoicesApi.endpoints.getRefundOperationStatus,
  invoicesApi.endpoints.getRefundRecommendationHistory,
  refundWorkflowApi.endpoints.getRefundWorkflow,
  invoicesApi.endpoints.refundInvoice,
  invoicesApi.endpoints.submitRefundOperation,
  // The refund forms read the supervisor threshold on every submit. When the read
  // fails they ask for a supervisor and say why, so a toast would only repeat it.
  invoicesApi.endpoints.getRefundSupervisorApprovalThreshold,
  // Shop-page mutations own their error toasts (conflict guidance plus a
  // generic fallback via getShopPageActionErrorMessage). Suppress the global
  // handler so a 409 with a backend message is not toasted twice.
  // The component registry is also the backend-owned availability probe. A
  // rejected probe means the rollout is hidden; polling it must stay silent.
  shopPagesApi.endpoints.createShopPage,
  shopPagesApi.endpoints.deleteShopPage,
  shopPagesApi.endpoints.getShopPageComponents,
  shopPagesApi.endpoints.publishShopPage,
  shopPagesApi.endpoints.publishShopHomeRouting,
  shopPagesApi.endpoints.pullShopHomeRouting,
  shopPagesApi.endpoints.refreshShopPageFromContentful,
  shopPagesApi.endpoints.refreshShopPageConflict,
  shopPagesApi.endpoints.resolveShopPageSync,
  shopPagesApi.endpoints.restoreDeletedShopPage,
  shopPagesApi.endpoints.retryShopHomeRoutingSync,
  shopPagesApi.endpoints.retryShopPageSync,
  shopPagesApi.endpoints.detachShopPageSync,
  shopPagesApi.endpoints.unpublishShopHomeRouting,
  shopPagesApi.endpoints.unpublishShopPage,
  shopPagesApi.endpoints.updateShopHomeRouting,
  shopPagesApi.endpoints.updateShopPage,
  // Page list and routing queries poll and render inline error + retry
  // affordances; a persistent backend error would otherwise toast every cycle.
  shopPagesApi.endpoints.getShopPages,
  shopPagesApi.endpoints.getShopHomeRouting,
  // Shop-segment mutations own their error toasts (version-conflict guidance
  // plus explicit fallbacks in ShopSegments). Suppress the global handler so a
  // 409 with a backend message is not toasted twice.
  shopSegmentsApi.endpoints.createShopSegment,
  shopSegmentsApi.endpoints.deleteShopSegment,
  shopSegmentsApi.endpoints.publishShopSegment,
  shopSegmentsApi.endpoints.retryShopSegmentSync,
  shopSegmentsApi.endpoints.updateShopSegment,
  // Segment list/attributes failures render inline retry affordances on the
  // page; the list also polls, so a persistent backend error would otherwise
  // toast every poll cycle.
  shopSegmentsApi.endpoints.getShopSegmentAttributes,
  shopSegmentsApi.endpoints.getShopSegments
];

// The generic connection-error fallback below fires only for shop content
// mutations. Every other feature's mutation call sites (~27 catch blocks)
// print their own message-less-failure fallbacks, so a global toast would
// double up with theirs. Shop mutations in expectedActions never reach the
// fallback either (their local catches own the toast) — in practice this list
// covers the shop mutations outside that list (e.g. uploadShopAsset) and any
// endpoint later removed from it.
// NOTE: enumerated explicitly (matching expectedActions) because
// `Object.keys(shopPagesApi.endpoints)` is not a sound source here —
// `injectEndpoints` returns the shared apiSlice object, so `.endpoints`
// contains every endpoint injected anywhere in the app, not just this slice's.
const shopFallbackToastEndpoints = [
  shopPagesApi.endpoints.createShopPage,
  shopPagesApi.endpoints.deleteShopPage,
  shopPagesApi.endpoints.detachShopPageSync,
  shopPagesApi.endpoints.publishShopHomeRouting,
  shopPagesApi.endpoints.publishShopPage,
  shopPagesApi.endpoints.pullShopHomeRouting,
  shopPagesApi.endpoints.refreshShopPageConflict,
  shopPagesApi.endpoints.refreshShopPageFromContentful,
  shopPagesApi.endpoints.resolveShopPageSync,
  shopPagesApi.endpoints.restoreDeletedShopPage,
  shopPagesApi.endpoints.retryShopHomeRoutingSync,
  shopPagesApi.endpoints.retryShopPageSync,
  shopPagesApi.endpoints.unpublishShopHomeRouting,
  shopPagesApi.endpoints.unpublishShopPage,
  shopPagesApi.endpoints.updateShopHomeRouting,
  shopPagesApi.endpoints.updateShopPage,
  shopPagesApi.endpoints.uploadShopAsset,
  shopSegmentsApi.endpoints.createShopSegment,
  shopSegmentsApi.endpoints.deleteShopSegment,
  shopSegmentsApi.endpoints.publishShopSegment,
  shopSegmentsApi.endpoints.retryShopSegmentSync,
  shopSegmentsApi.endpoints.updateShopSegment
];

export const criticalEndpoints = [
  apiSlice.endpoints.getMyProfile,
  apiSlice.endpoints.updateMyProfile,
  apiSlice.endpoints.updateProfileImage,
  authApi.endpoints.getUserProfileAndRoles,
  crossSellApi.endpoints.createAccount,
  patientPaymentApi.endpoints.submitInsurance,
  invoicesApi.endpoints.refundInvoice,
  patientSubscriptionApi.endpoints.cancelSubscriptionAutoRenew,
  patientSubscriptionApi.endpoints.deactivateWithRefund,
  patientSubscriptionApi.endpoints.downgradeSubscription,
  patientSubscriptionApi.endpoints.pauseSubscription,
  patientSubscriptionApi.endpoints.resumeSubscription,
  patientPaymentApi.endpoints.updatePaymentProfile,
  patientPaymentApi.endpoints.createPaymentProfile,
  patientSubscriptionApi.endpoints.changeSubscription,
  tasksApi.endpoints.getTasks,
  tasksApi.endpoints.getTaskDetails,
  patientsApi.endpoints.getPatients,
  patientsApi.endpoints.getPatient
];

export const rtkQueryErrorLogger: Middleware = () => (next) => (action) => {
  const isExpectedAction = expectedActions.some((expectedAction) =>
    expectedAction.matchRejected(action)
  );

  // const isCriticalEndpoint = criticalEndpoints.some((criticalEndpoint) =>
  //   criticalEndpoint.matchRejected(action)
  // );

  // CDS-block 422s from any submit path carry their guidance in `cards`;
  // the caller handles them inline (safety-net banner). Skip the global toast.
  const isCdsBlockPayload = isRejectedWithValue(action) && isCdsBlock(action.payload);

  const isPdmpAttestationRejected = pdmpApi.endpoints.attestPrerequisite.matchRejected(action);

  // The idempotency guard's `message` describes the guard, not anything the operator did; the submitting
  // component toasts its own copy instead.
  const isIdempotencyGuardPayload =
    isRejectedWithValue(action) && !!getIdempotencyGuardCode(action.payload);

  // A missing supervisor name is answered by the approval popup the submitting form opens. The
  // downgrade and deactivation endpoints still toast their other failures, so match the payload.
  const isSupervisorApprovalPayload =
    isRejectedWithValue(action) && !!readRefundSupervisorApprovalRejection(action.payload);

  if (
    !isExpectedAction &&
    !isCdsBlockPayload &&
    !isPdmpAttestationRejected &&
    !isIdempotencyGuardPayload &&
    !isSupervisorApprovalPayload &&
    isRejectedWithValue(action)
  ) {
    const actionPayload = action.payload as Action['payload'];
    const errorMessage =
      'data' in actionPayload ? actionPayload.data?.message : actionPayload?.message;

    // Log only errors that are not logged by the backend
    // if ('status' in actionPayload && actionPayload.status === 'FETCH_ERROR') {
    //   const level = getErrorLevel(actionPayload, isCriticalEndpoint);

    //   logError(
    //     new Error(getErrorMessage(actionPayload, errorMessage)),
    //     {
    //       componentStack: 'rtkQueryErrorLogger',
    //       ...actionPayload
    //     },
    //     level
    //   );
    // }

    if (errorMessage) {
      notifyError(errorMessage);
    } else if (
      (action as Action).meta?.arg?.type === 'mutation' &&
      shopFallbackToastEndpoints.some((endpoint) => endpoint.matchRejected(action))
    ) {
      // A shop mutation that failed without an extractable message (network
      // failure, empty 5xx) was an explicit user action — silence would read
      // as success. Queries stay silent: polling reads (shop lists, the
      // availability probe) would otherwise toast every failed cycle. Non-shop
      // mutations also stay silent here: their call sites print their own
      // fallbacks, and this toast would double up with them.
      notifyError('The request could not be completed. Check your connection and try again.');
    }
  }

  return next(action);
};
